import { format } from "date-fns";
import { formatInTimeZone, utcToZonedTime } from "date-fns-tz";
import { getTimeZoneName, getTimeZoneOffsetMs, isLocalTimeZone, normalizeIANATimeZone, normalizeTimeZone } from "./time-zone-utils.js";
import { ONE_DAY, ONE_HOUR, ONE_MINUTE, ONE_SECOND, ONE_YEAR } from "./constants.js";
const DATE_FORMAT = 'MMM dd, yyyy';
const TIME_FORMATS = {
    '12h': {
        withSeconds: 'hh:mm:ss a',
        withoutSeconds: 'hh:mm a',
        withMs: 'hh:mm:ss.SSS a'
    },
    '24h': {
        withSeconds: 'HH:mm:ss',
        withoutSeconds: 'HH:mm',
        withMs: 'HH:mm:ss.SSS'
    }
};
const getTimeFormat = ({ timeFormat, includeMs = false, omitSeconds = false })=>{
    if (includeMs) return TIME_FORMATS[timeFormat].withMs;
    if (omitSeconds) return TIME_FORMATS[timeFormat].withoutSeconds;
    return TIME_FORMATS[timeFormat].withSeconds;
};
const getDateFormat = ({ includeDayOfWeek = false })=>includeDayOfWeek ? `EEE, ${DATE_FORMAT}` : DATE_FORMAT;
const getDateTimeFormat = (options)=>`${getDateFormat(options)}, ${getTimeFormat(options)}`;
function getFormattedDateTimeString({ date, timeZone, includeMs, omitCommas, omitSeconds, omitTimeZoneAbbreviation, timeFormat, includeDayOfWeek }) {
    let dateFormat = getDateTimeFormat({
        timeFormat,
        includeMs,
        omitSeconds,
        includeDayOfWeek
    });
    if (omitCommas) dateFormat = dateFormat.replace(/,/g, '');
    try {
        const timeZoneAbbreviation = omitTimeZoneAbbreviation ? '' : ` ${getTimeZoneName(timeZone, {
            variant: 'short'
        })}`;
        return `${formatWithTimeZone(new Date(date), dateFormat, timeZone)}${timeZoneAbbreviation}`;
    } catch (error) {
        console.warn('Failed to format date. `date` or `timeZone` is likely invalid. Error: ', error);
        return '';
    }
}
function getFormattedDateString({ date, timeZone, includeDayOfWeek }) {
    try {
        return formatWithTimeZone(new Date(date), getDateFormat({
            includeDayOfWeek
        }), timeZone);
    } catch (error) {
        console.warn('Failed to format date. `date` or `timeZone` is likely invalid. Error: ', error);
        return '';
    }
}
function getFormattedTimeString({ date, timeZone, includeMs, omitSeconds, omitTimeZoneAbbreviation, timeFormat = '24h' }) {
    const dateFormat = getTimeFormat({
        timeFormat,
        includeMs,
        omitSeconds
    });
    try {
        const timeZoneAbbreviation = omitTimeZoneAbbreviation ? '' : ` ${getTimeZoneName(timeZone, {
            variant: 'short'
        })}`;
        return `${formatWithTimeZone(new Date(date), dateFormat, timeZone)}${timeZoneAbbreviation}`;
    } catch (error) {
        console.warn('Failed to format date. `date` or `timeZone` is likely invalid. Error: ', error);
        return '';
    }
}
function getFormattedDateTimeRange({ startDate, endDate, timeZone, omitSeconds, timeFormat, includeDayOfWeek }) {
    const formattedMinDate = getFormattedDateTimeString({
        date: startDate,
        timeZone,
        omitSeconds,
        omitTimeZoneAbbreviation: true,
        timeFormat,
        includeDayOfWeek
    });
    const formattedMaxDate = getFormattedDateTimeString({
        date: endDate,
        timeZone,
        omitSeconds,
        timeFormat,
        includeDayOfWeek
    });
    if ('' === formattedMinDate || '' === formattedMaxDate) return '';
    return `${formattedMinDate} - ${formattedMaxDate}`;
}
function formatWithTimeZone(date, formatString, timeZone) {
    const normalizedTimeZone = normalizeTimeZone(timeZone);
    if (isLocalTimeZone(normalizedTimeZone)) return format(date, formatString);
    return formatInTimeZone(date, normalizedTimeZone, formatString);
}
function padTime(value) {
    return value.toString().padStart(2, '0');
}
function formatTimeZoneOffset(timeZone, { variant = 'long' } = {}) {
    const offsetMs = getTimeZoneOffsetMs(timeZone);
    if (0 === offsetMs) return 'UTC';
    const sign = offsetMs > 0 ? '+' : '-';
    const hours = Math.floor(Math.abs(offsetMs) / ONE_HOUR);
    const minutes = Math.floor(Math.abs(offsetMs) % ONE_HOUR / ONE_MINUTE);
    let result = 'UTC' + sign;
    if ('short' === variant) result += hours;
    else result += padTime(hours);
    if ('short' !== variant || minutes > 0) result += ':' + padTime(minutes);
    return result;
}
function formatAgo(thenMillis, nowMillis) {
    const diffMillis = Math.trunc(nowMillis - thenMillis);
    if (diffMillis <= 0) return 'now';
    if (diffMillis < ONE_SECOND) return `${diffMillis}ms ago`;
    if (diffMillis <= 120 * ONE_SECOND) return `${Math.trunc(diffMillis / ONE_SECOND)}s ago`;
    if (diffMillis <= 2 * ONE_HOUR) return `${Math.trunc(diffMillis / ONE_MINUTE)}m ago`;
    if (diffMillis <= 2 * ONE_DAY) {
        const hours = Math.trunc(diffMillis / ONE_HOUR);
        return `${hours}h${Math.trunc((diffMillis - hours * ONE_HOUR) / ONE_MINUTE)}m ago`;
    } else if (diffMillis <= 1825 * ONE_DAY) {
        const days = Math.trunc(diffMillis / ONE_DAY);
        return `${days}d${Math.trunc((diffMillis - days * ONE_DAY) / ONE_HOUR)}h ago`;
    } else {
        const years = Math.trunc(diffMillis / ONE_YEAR);
        return `${years}y${Math.trunc((diffMillis - years * ONE_YEAR) / ONE_DAY)}d ago`;
    }
}
function formatDurationCompact(x) {
    if (x < 1.0) return `${parseInt(String(1000 * x), 10)}ms`;
    if (x < 60.0) return `${x.toFixed(1)}s`;
    if (x < 3600.0) {
        const minutes = Math.floor(x / 60);
        const seconds = Math.floor(x % 60);
        return `${minutes}m ${seconds}s`;
    }
    {
        const hours = Math.floor(x / 3600);
        const minutes = Math.floor(x % 3600 / 60);
        return `${hours}h ${minutes}m`;
    }
}
function formatMicroseconds(microseconds) {
    if (microseconds > 1000000) return `${(microseconds / 1000000).toFixed(2)}s`;
    if (microseconds > 1000) return `${(microseconds / 1000).toFixed(2)}ms`;
    return `${microseconds}μs`;
}
function parseSecondsSinceEpoch(seconds, nowFn) {
    const millisSinceEpoch = 1000.0 * seconds;
    const date = new Date(millisSinceEpoch);
    const isoString = date.toISOString();
    return {
        date,
        agoStr: formatAgo(millisSinceEpoch, (nowFn && nowFn().getTime()) ?? new Date().getTime()),
        isoString
    };
}
const convertHoursToDays = (rawHours)=>{
    if (null == rawHours || '' === rawHours) return '';
    const totalHours = Number(rawHours.split('h')[0]);
    const fullDays = Math.trunc(totalHours / 24);
    const remainingHours = totalHours - 24 * fullDays;
    if (remainingHours > 0) return `${fullDays}d ${remainingHours}h`;
    return `${fullDays}d`;
};
function dateTimetoEpoch(dateTimeStr) {
    return new Date(dateTimeStr).getTime() / 1000;
}
function getNowInTimeZone(timeZone) {
    const now = new Date();
    if (!timeZone) return now;
    try {
        return utcToZonedTime(now, normalizeIANATimeZone(timeZone));
    } catch  {
        return now;
    }
}
function formatMsDuration(durationMs) {
    if (durationMs < 1000) return `${durationMs}ms`;
    if (durationMs < 60000) return `${(durationMs / 1000).toFixed(0)}s`;
    if (durationMs < 3600000) return `${(durationMs / 60000).toFixed(0)}m`;
    if (durationMs < 86400000) return `${(durationMs / 3600000).toFixed(0)}h`;
    return `${(durationMs / 86400000).toFixed(0)}d`;
}
export { DATE_FORMAT, convertHoursToDays, dateTimetoEpoch, formatAgo, formatDurationCompact, formatMicroseconds, formatMsDuration, formatTimeZoneOffset, formatWithTimeZone, getDateFormat, getDateTimeFormat, getFormattedDateString, getFormattedDateTimeRange, getFormattedDateTimeString, getFormattedTimeString, getNowInTimeZone, getTimeFormat, parseSecondsSinceEpoch };

//# sourceMappingURL=formatters.js.map